import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';
import themeFrameMappingApi from '@services/themeFrameMappingApi.jsx';
import themeFilterMappingApi from '@services/themeFilterMappingApi.jsx';
import '@assets/js/common/table';
import '@assets/js/common/modal';
import '@assets/css/common/style_modal.css';
import FilterContentsAdd from '@assets/js/modals/FilterContentsAdd';
import FrameContentsAdd from '@assets/js/modals/FrameContentsAdd';
import { getTimerRedirectOptions, migrateTimerRedirectValue } from '../utils/pageNavigationUtils';

const PresetPhotoSelectionModeSetting = ({ preset, presetPageMasters }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFrames, setIsLoadingFrames] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetPhotoSelectionPage, setPresetPhotoSelectionPage] = useState(null);

    // 폼 데이터 상태
  const [formData, setFormData] = useState({
    timerSeconds: 10,
    timerTimeoutRedirectUrl: 'start',
    isActive: true
  });

  // 타이머 관련
  const [timer, setTimer] = useState(10);
  const [timeOverPage, setTimeOverPage] = useState('start');
  
  // 타이머 리다이렉트 옵션
  const [timerRedirectOptions, setTimerRedirectOptions] = useState([]);

  // 테마 프레임/필터 목록
  const [themeFrames, setThemeFrames] = useState([]);
  const [themeFilters, setThemeFilters] = useState([]);
  const [frames, setFrames] = useState([]);
  const [filters, setFilters] = useState([]);

  // 전체 선택 상태
  const [selectAllFrames, setSelectAllFrames] = useState(false);
  const [selectAllFilters, setSelectAllFilters] = useState(false);

  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMode, setModalMode] = useState('add');
  const [editTargetFrame, setEditTargetFrame] = useState(null);
  const [editTargetFilter, setEditTargetFilter] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // 페이지 데이터 로드
  useEffect(() => {
    loadPhotoSelectionPageData();
  }, [presetId]);

  useEffect(() => {
    if(presetPhotoSelectionPage?.themeId
        && presetPhotoSelectionPage?.themePageMasterId)
    {
      loadThemeFrames();
      loadThemeFilters();
    }
  }, [presetPhotoSelectionPage]);
  
  // 타이머 리다이렉트 옵션 업데이트
  useEffect(() => {
    if (presetPageMasters && presetPageMasters.length > 0) {
      const options = getTimerRedirectOptions('select_mode', presetPageMasters);
      setTimerRedirectOptions(options);
    }
  }, [presetPageMasters]);

  // 사진 선택 페이지 데이터 로드
  const loadPhotoSelectionPageData = async () => {
    try {
      setIsLoading(true);
      const photoSelectionPage = await presetApi.getPhotoSelectionPage(presetId);
      
      setPresetPhotoSelectionPage(photoSelectionPage);
      
      // 기존 timerTimeoutRedirectUrl 값을 새로운 형식으로 마이그레이션
      const migratedRedirectValue = migrateTimerRedirectValue(
        photoSelectionPage.timerTimeoutRedirectUrl, 
        'select_mode'
      );
      
      setFormData({
        timerSeconds: photoSelectionPage.timerSeconds ?? 10,
        timerTimeoutRedirectUrl: migratedRedirectValue,
        isActive: photoSelectionPage.isActive ?? true
      });
      
      setTimer(photoSelectionPage.timerSeconds ?? 10);
      setTimeOverPage(migratedRedirectValue);
    } catch (error) {
      console.error('사진 선택 페이지 데이터 로드 실패:', error);
      // 에러 시 기본값 유지
    } finally {
      setIsLoading(false);
    }
  };

  // 테마 프레임 목록 로드
  const loadThemeFrames = async () => {
    try {
      setIsLoadingFrames(true);
      const mappings = await themeFrameMappingApi.getThemeFrameMappings(presetPhotoSelectionPage?.themeId, presetPhotoSelectionPage?.themePageMasterId);

      const formattedFrames = mappings.map((mapping, index) => ({
        id: mapping.id,
        mappingId: mapping.id,
        photoFrameId: mapping.photoFrameId,
        name: mapping.photoFrameName || mapping.frameName,
        type: mapping.frameType || '프레임',
        direction: mapping.frameDirection || '가로',
        selected: false,
        frameImage1: mapping.customDefaultImageViewUrl || mapping.customDefaultImageDownloadUrl,
        frameImage2: mapping.customHoverImageViewUrl || mapping.customHoverImageDownloadUrl,
        displayOrder: mapping.displayOrder,
        isActive: mapping.isActive,
        themeId: mapping.themeId
      }));

      setThemeFrames(formattedFrames);
      setFrames(formattedFrames);
    } catch (error) {
      console.error('테마 프레임 목록 로드 실패:', error);
      setThemeFrames([]);
      setFrames([]);
    } finally {
      setIsLoadingFrames(false);
    }
  };

  // 테마 필터 목록 로드
  const loadThemeFilters = async () => {
    try {
      setIsLoadingFilters(true);
      const mappings = await themeFilterMappingApi.getThemeFilterMappings(presetPhotoSelectionPage?.themeId, presetPhotoSelectionPage?.themePageMasterId);

      const formattedFilters = mappings.map((mapping, index) => ({
        id: mapping.id,
        mappingId: mapping.id,
        photoFilterId: mapping.photoFilterId,
        name: mapping.photoFilterName || mapping.filterName,
        type: '필터',
        selected: false,
        filterImage1: mapping.customDefaultImageViewUrl || mapping.customDefaultImageDownloadUrl,
        filterImage2: mapping.customHoverImageViewUrl || mapping.customHoverImageDownloadUrl,
        displayOrder: mapping.displayOrder,
        isActive: mapping.isActive,
        themeId: mapping.themeId
      }));

      setThemeFilters(formattedFilters);
      setFilters(formattedFilters);
    } catch (error) {
      console.error('테마 필터 목록 로드 실패:', error);
      setThemeFilters([]);
      setFilters([]);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  // 타이머 조절 핸들러
  const handleTimerChange = (action) => {
    let newTimer;
    if (action === 'increase') {
      newTimer = Math.min(timer + 1, 60);
    } else {
      newTimer = Math.max(timer - 1, 1);
    }
    
    setTimer(newTimer);
    setFormData({
      ...formData,
      timerSeconds: newTimer
    });
  };
  
  // 타임 오버 페이지 변경 핸들러
  const handleTimeOverPageChange = (e) => {
    const newPage = e.target.value;
    setTimeOverPage(newPage);
    setFormData({
      ...formData,
      timerTimeoutRedirectUrl: newPage
    });
  };

  // 체크박스 선택 핸들러
  const handleCheckboxChange = (type, id) => {
    if (type === 'frame') {
      setFrames(prevFrames =>
        prevFrames.map(frame =>
          frame.id === id ? { ...frame, selected: !frame.selected } : frame
        )
      );
    } else if (type === 'filter') {
      setFilters(prevFilters =>
        prevFilters.map(filter =>
          filter.id === id ? { ...filter, selected: !filter.selected } : filter
        )
      );
    }
  };

  // 전체 선택 핸들러
  const handleSelectAll = (type) => {
    if (type === 'frame') {
      setSelectAllFrames(!selectAllFrames);
      setFrames(prevFrames =>
        prevFrames.map(frame => ({ ...frame, selected: !selectAllFrames }))
      );
    } else if (type === 'filter') {
      setSelectAllFilters(!selectAllFilters);
      setFilters(prevFilters =>
        prevFilters.map(filter => ({ ...filter, selected: !selectAllFilters }))
      );
    }
  };

  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowDeleteModal(false);
    setShowEditModal(false);
    setModalMode('add');
    setEditTargetFrame(null);
    setEditTargetFilter(null);
    setDeleteTarget(null);
  };

  // 수정 버튼 클릭 핸들러
  const handleEditClick = (type, item) => {
    setModalType(type);
    if (type === 'frame') {
      setEditTargetFrame(item);
    } else {
      setEditTargetFilter(item);
    }
    setShowEditModal(true);
  };

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = (type, item) => {
    setModalType(type);
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  // 삭제 확인 핸들러
  const handleConfirmDelete = () => {
    if (modalType === 'frame') {
      setFrames(prevFrames => prevFrames.filter(frame => frame.id !== deleteTarget.id));
    } else {
      setFilters(prevFilters => prevFilters.filter(filter => filter.id !== deleteTarget.id));
    }
    handleCloseModal();
  };

  // 프레임 저장 핸들러
  const handleSaveFrame = (data) => {
    if (modalMode === 'edit') {
      setFrames(prevFrames =>
        prevFrames.map(frame =>
          frame.id === editTargetFrame.id
            ? {
                ...frame,
                name: data.name,
                type: data.type === '1cut_hor&2cut_ver' ? '1컷 가로 & 2컷 세로' :
                      data.type === '1cut_ver&2cut_hor' ? '1컷 세로 & 2컷 가로' :
                      data.type === '3cut_ver&4cut_ver' ? '3컷 세로 & 4컷 세로' :
                      '3컷 가로 & 4컷 가로',
                direction: data.direction === 'landscape' ? '가로' : '세로'
              }
            : frame
        )
      );
    } else {
      const newFrame = {
        id: frames.length + 1,
        name: data.name,
        type: data.type === '1cut_hor&2cut_ver' ? '1컷 가로 & 2컷 세로' :
              data.type === '1cut_ver&2cut_hor' ? '1컷 세로 & 2컷 가로' :
              data.type === '3cut_ver&4cut_ver' ? '3컷 세로 & 4컷 세로' :
              '3컷 가로 & 4컷 가로',
        direction: data.direction === 'landscape' ? '가로' : '세로',
        selected: false
      };
      setFrames(prev => [...prev, newFrame]);
    }
    handleCloseModal();
  };

  // 필터 저장 핸들러
  const handleSaveFilter = (data) => {
    if (modalMode === 'edit') {
      setFilters(prevFilters =>
        prevFilters.map(filter =>
          filter.id === editTargetFilter.id
            ? {
                ...filter,
                name: data.name
              }
            : filter
        )
      );
    } else {
      const newFilter = {
        id: filters.length + 1,
        name: data.name,
        type: '필터',
        selected: false
      };
      setFilters(prev => [...prev, newFilter]);
    }
    handleCloseModal();
  };

  // 상세정보 모달 핸들러
  const handleInfoClick = (type, item) => {
    setCurrentItem(item);
    setModalType(type);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setCurrentItem(null);
  };

  // 저장 이벤트 리스너
  useEffect(() => {
    const eventName = `savePagePreset_select_mode`;

    const saveHandler = async (event) => {
      if (!preset?.id) {
        alert("프리셋 정보가 없습니다.");
        return;
      }

      try {
        setIsLoading(true);

        const dataToSave = {
          presetId: event.detail.presetId,
          timerSeconds: formData.timerSeconds,
          timerTimeoutRedirectUrl: formData.timerTimeoutRedirectUrl,
          isActive: formData.isActive
        };
        
        const response = await presetApi.savePhotoSelectionPage(event.detail.presetId, dataToSave);
        console.log("사진 선택 페이지 저장 성공:", response);

      } catch (error) {
        console.error("사진 선택 페이지 저장 실패:", error);
        alert("사진 선택 페이지 저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    document.addEventListener(eventName, saveHandler);

    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [preset?.id, formData]);

  // 테이블 초기화
  useEffect(() => {
    if (window.tableManager) {
      window.tableManager.initTable({
        tableSelector: '.common-table',
        filterSelector: '#frame-filter, #photo-filter-filter',
        columnWidthOptions: {
          equalWidth: false,
          firstColumnWidth: 5,
          lastColumnWidth: 5,
          fixedColumns: {
            1: 10,  // NO 열
            2: 20,  // 이름 열
            3: 15,  // 타입 열
            4: 10,  // 방향 열 (프레임만)
            5: 10,  // 상세정보 열
          }
        }
      });
    }
  }, [frames, filters]);

  if (isLoading) {
    return <div className="preset-settings">로딩 중...</div>;
  }

  return (
    <div className="preset-settings">
      {/* 타이머 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">타이머</h4>
        <div className="settings-row">
          <div className="settings-label">타이머 시간</div>
          <div className="timer-control-container">
            <button 
              className="timer-btn" 
              onClick={() => handleTimerChange('decrease')}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="timer-input" 
              value={timer}
              readOnly
            />
            <button 
              className="timer-btn"
              onClick={() => handleTimerChange('increase')}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="timer-unit">초</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">타임 오버 이동 페이지</div>
          <div className="page-select-container">
            <select 
              className="page-select"
              value={timeOverPage}
              onChange={handleTimeOverPageChange}
            >
              {timerRedirectOptions.map(option => (
                <option 
                  key={option.value} 
                  value={option.value}
                  disabled={!option.available}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div>

      {/* 프레임 섹션 */}
      <div className="data-section-header">
        <div className="data-section-title">
          <h4 className="section-title">프레임 목록</h4>
          {isLoadingFrames && <span className="loading-text">(로딩 중...)</span>}
        </div>
        <div className="filter-dropdown-container">
          <div className="filter-dropdown">
            <select id="frame-filter">
              <option value="all">전체</option>
              <option value="1cut_hor&2cut_ver">1컷 가로 & 2컷 세로</option>
              <option value="1cut_ver&2cut_hor">1컷 세로 & 2컷 가로</option>
              <option value="3cut_ver&4cut_ver">3컷 세로 & 4컷 세로</option>
              <option value="3cut_hor&4cut_hor">3컷 가로 & 4컷 가로</option>
            </select>
          </div>
        </div>
      </div>

      {/* 프레임 테이블 */}
      <div className="common-table-container">
        <table className="common-table">
          <thead>
            <tr>
              {/*<th>*/}
              {/*  <div className="checkbox-container">*/}
              {/*    <input */}
              {/*      type="checkbox" */}
              {/*      id="select-all-frames"*/}
              {/*      checked={selectAllFrames}*/}
              {/*      onChange={() => handleSelectAll('frame')}*/}
              {/*    />*/}
              {/*    <label htmlFor="select-all-frames" className="custom-checkbox"></label>*/}
              {/*  </div>*/}
              {/*</th>*/}
              <th>NO</th>
              <th>프레임명</th>
              <th>타입</th>
              <th>방향</th>
              <th>상세정보</th>
            </tr>
          </thead>
          <tbody>
            {frames.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  {isLoadingFrames ? '프레임 목록을 로딩 중입니다...' : '등록된 프레임이 없습니다.'}
                </td>
              </tr>
            ) : (
              frames.map((frame, index) => (
                <tr key={frame.id}>
                  {/*<td>*/}
                  {/*  <div className="checkbox-container">*/}
                  {/*    <input */}
                  {/*      type="checkbox" */}
                  {/*      id={`frame-${frame.id}`}*/}
                  {/*      checked={frame.selected}*/}
                  {/*      onChange={() => handleCheckboxChange('frame', frame.id)}*/}
                  {/*    />*/}
                  {/*    <label htmlFor={`frame-${frame.id}`} className="custom-checkbox"></label>*/}
                  {/*  </div>*/}
                  {/*</td>*/}
                  <td>{index + 1}</td>
                  <td>{frame.name}</td>
                  <td>{frame.type}</td>
                  <td>{frame.direction}</td>
                  <td>
                    <button className="info-btn" onClick={() => handleInfoClick('frame', frame)}>
                      <i className="ri-information-line"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 필터 섹션 */}
      <div className="data-section-header">
        <div className="data-section-title">
          <h4 className="section-title">필터 목록</h4>
          {isLoadingFilters && <span className="loading-text">(로딩 중...)</span>}
        </div>
        <div className="filter-dropdown-container">
          <div className="filter-dropdown">
            <select id="photo-filter-filter">
              <option value="all">전체</option>
              <option value="photo-filter">필터</option>
            </select>
          </div>
        </div>
      </div>

      {/* 필터 테이블 */}
      <div className="common-table-container">
        <table className="common-table">
          <thead>
            <tr>
              {/*<th>*/}
              {/*  <div className="checkbox-container">*/}
              {/*    <input */}
              {/*      type="checkbox" */}
              {/*      id="select-all-filters"*/}
              {/*      checked={selectAllFilters}*/}
              {/*      onChange={() => handleSelectAll('filter')}*/}
              {/*    />*/}
              {/*    <label htmlFor="select-all-filters" className="custom-checkbox"></label>*/}
              {/*  </div>*/}
              {/*</th>*/}
              <th>NO</th>
              <th>필터명</th>
              <th>타입</th>
              <th>상세정보</th>
            </tr>
          </thead>
          <tbody>
            {filters.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center">
                  {isLoadingFilters ? '필터 목록을 로딩 중입니다...' : '등록된 필터가 없습니다.'}
                </td>
              </tr>
            ) : (
              filters.map((filter, index) => (
                <tr key={filter.id}>
                  {/*<td>*/}
                  {/*  <div className="checkbox-container">*/}
                  {/*    <input */}
                  {/*      type="checkbox" */}
                  {/*      id={`filter-${filter.id}`}*/}
                  {/*      checked={filter.selected}*/}
                  {/*      onChange={() => handleCheckboxChange('filter', filter.id)}*/}
                  {/*    />*/}
                  {/*    <label htmlFor={`filter-${filter.id}`} className="custom-checkbox"></label>*/}
                  {/*  </div>*/}
                  {/*</td>*/}
                  <td>{index + 1}</td>
                  <td>{filter.name}</td>
                  <td>{filter.type}</td>
                  <td>
                    <button className="info-btn" onClick={() => handleInfoClick('filter', filter)}>
                      <i className="ri-information-line"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 모달 */}
      {showAddModal && (
        modalType === 'frame' ? (
          <FrameContentsAdd 
            onClose={handleCloseModal}
            onSave={handleSaveFrame}
            initialData={editTargetFrame}
            isEditMode={modalMode === 'edit'}
          />
        ) : (
          <FilterContentsAdd 
            onClose={handleCloseModal}
            onSave={handleSaveFilter}
            initialData={editTargetFilter}
            isEditMode={modalMode === 'edit'}
          />
        )
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay active">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">{modalType === 'frame' ? '프레임' : '필터'} 삭제</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>이 {modalType === 'frame' ? '프레임' : '필터'}을(를) 삭제하시겠습니까?</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel" 
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button 
                className="modal-btn modal-btn-primary" 
                onClick={handleConfirmDelete}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {showEditModal && (
        modalType === 'frame' ? (
          <FrameContentsAdd 
            onClose={handleCloseModal}
            onSave={handleSaveFrame}
            initialData={editTargetFrame}
            isEditMode={true}
          />
        ) : (
          <FilterContentsAdd 
            onClose={handleCloseModal}
            onSave={handleSaveFilter}
            initialData={editTargetFilter}
            isEditMode={true}
          />
        )
      )}

      {/* 상세정보 모달 */}
      {showDetailModal && currentItem && (
        <div className="modal-overlay active" id={`${modalType}-detail-modal`}>
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">{modalType === 'frame' ? '프레임' : '필터'} 상세정보</h3>
              <button className="modal-close" onClick={handleCloseDetailModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-item-container">
                <span className="detail-label">{modalType === 'frame' ? '프레임명' : '필터명'}</span>
                <span className="detail-content">{currentItem.name}</span>
              </div>
              <div className="detail-item-container">
                <span className="detail-label">타입</span>
                <span className="detail-content">{currentItem.type}</span>
              </div>
              {modalType === 'frame' && (
                <div className="detail-item-container">
                  <span className="detail-label">방향</span>
                  <span className="detail-content">{currentItem.direction}</span>
                </div>
              )}
              <div className="detail-item-container">
                <div className="detail-row">
                  <div className="detail-column">
                    <span className="detail-label">{modalType === 'frame' ? '프레임 선택 이미지 (클릭 전)' : '필터 선택 이미지 (클릭 전)'}</span>
                    <span className="detail-content">{modalType === 'frame' ? currentItem.frameImage1 : currentItem.filterImage1}</span>
                    <div className={`modal-data-image${modalType === 'frame' ? '-frame' : ''}`}>
                      <img src={modalType === 'frame' ? (currentItem.frameImage1 || "images/frameData/frame1.png") : (currentItem.filterImage1 || "images/filterData/filter1.png")} alt={modalType} />
                    </div>
                  </div>
                  <div className="detail-column">
                    <span className="detail-label">{modalType === 'frame' ? '프레임 선택 이미지 (클릭 후)' : '필터 선택 이미지 (클릭 후)'}</span>
                    <span className="detail-content">{modalType === 'frame' ? currentItem.frameImage2 : currentItem.filterImage2}</span>
                    <div className={`modal-data-image${modalType === 'frame' ? '-frame' : ''}`}>
                      <img src={modalType === 'frame' ? (currentItem.frameImage2 || "images/frameData/frame2.png") : (currentItem.filterImage2 || "images/filterData/filter2.png")} alt={modalType} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-primary" 
                onClick={handleCloseDetailModal}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresetPhotoSelectionModeSetting;
